import { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Trash2, Loader2, Package, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCurrency } from '@/hooks/useCurrency';

/**
 * QuantityInput - a controlled text input for decimal quantities.
 * Uses a local string state so that intermediate values like "0." or "0.2" are
 * allowed while typing. The numeric value is committed on blur or Enter.
 */
function QuantityInput({
  value,
  onChange,
  disabled,
  unit,
}: {
  value: number;
  onChange: (val: number) => void;
  disabled?: boolean;
  unit?: string;
}) {
  const [localValue, setLocalValue] = useState(value.toString());

  // Sync from external value when it changes (e.g. after a save round-trip)
  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const commit = () => {
    const parsed = parseFloat(localValue);
    if (!isNaN(parsed) && parsed > 0) {
      onChange(parsed);
    } else {
      // Revert to previous valid value
      setLocalValue(value.toString());
    }
  };

  return (
    <div className="flex items-center gap-1 justify-end">
      <Input
        type="text"
        inputMode="decimal"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        className="h-8 text-right w-24"
        disabled={disabled}
      />
      {unit && (
        <span className="text-xs text-muted-foreground w-8">{unit}</span>
      )}
    </div>
  );
}

export interface ComponentProduct {
  id: string;
  name: string;
  price: number;
  cost: number | null;
  sku: string | null;
  image_url: string | null;
}

export interface RawItem {
  id: string;
  name: string;
  cost_per_unit: number | null;
  sku: string | null;
  unit_of_measure: string;
}

export interface CompositeComponent {
  id: string;
  componentProductId?: string;
  rawItemId?: string;
  componentProduct?: ComponentProduct;
  rawItem?: RawItem;
  quantity: number;
  quantityUnit?: string;
}

interface CompositeComponentEditorProps {
  components: CompositeComponent[];
  availableProducts: ComponentProduct[];
  availableRawItems?: RawItem[];
  onAddComponent: (itemId: string, quantity: number, isRawItem: boolean, rawItemData?: RawItem) => void;
  onUpdateQuantity: (componentId: string, quantity: number) => void;
  onRemoveComponent: (componentId: string) => void;
  onCreateRawItem?: (name: string) => Promise<RawItem | null>;
  isLoading?: boolean;
  disabled?: boolean;
}

export function CompositeComponentEditor({
  components,
  availableProducts,
  availableRawItems = [],
  onAddComponent,
  onUpdateQuantity,
  onRemoveComponent,
  onCreateRawItem,
  isLoading = false,
  disabled = false,
}: CompositeComponentEditorProps) {
  const { formatCurrency } = useCurrency();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newQuantity, setNewQuantity] = useState('1');
  const [searchTab, setSearchTab] = useState<'products' | 'raw'>('products');
  const [creatingRawItem, setCreatingRawItem] = useState(false);

  // Filter out already-added products
  const filteredProducts = useMemo(() => {
    const addedIds = new Set(components.filter(c => c.componentProductId).map(c => c.componentProductId));
    return availableProducts.filter(
      p => !addedIds.has(p.id) && 
           p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [availableProducts, components, searchQuery]);

  // Filter out already-added raw items
  const filteredRawItems = useMemo(() => {
    const addedIds = new Set(components.filter(c => c.rawItemId).map(c => c.rawItemId));
    return availableRawItems.filter(
      item => !addedIds.has(item.id) && 
              item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [availableRawItems, components, searchQuery]);

  // Calculate totals
  const totals = useMemo(() => {
    return components.reduce(
      (acc, comp) => {
        let cost = 0;
        if (comp.componentProduct) {
          cost = comp.componentProduct.cost || comp.componentProduct.price;
        } else if (comp.rawItem) {
          cost = comp.rawItem.cost_per_unit || 0;
        }
        return {
          quantity: acc.quantity + comp.quantity,
          cost: acc.cost + (cost * comp.quantity),
        };
      },
      { quantity: 0, cost: 0 }
    );
  }, [components]);

  const handleQuickAdd = (itemId: string, isRawItem: boolean) => {
    const qty = parseFloat(newQuantity);
    if (qty > 0) {
      onAddComponent(itemId, qty, isRawItem);
      setSearchQuery('');
    }
  };

  const handleCreateAndAddRawItem = async () => {
    if (!onCreateRawItem || !searchQuery.trim()) return;
    setCreatingRawItem(true);
    try {
      const newItem = await onCreateRawItem(searchQuery.trim());
      if (newItem) {
        const qty = parseFloat(newQuantity);
        if (qty > 0) {
          onAddComponent(newItem.id, qty, true, newItem);
          setSearchQuery('');
        }
      }
    } finally {
      setCreatingRawItem(false);
    }
  };

  const hasRawItems = availableRawItems.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base font-medium flex items-center gap-2">
            <Package className="h-4 w-4" />
            Components
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Add products or raw materials to create a composite product.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowSearch(!showSearch)}
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Component
        </Button>
      </div>

      {/* Search & Add Section */}
      {showSearch && (
        <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
          {hasRawItems && (
            <Tabs value={searchTab} onValueChange={(v) => setSearchTab(v as 'products' | 'raw')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="products" className="gap-2">
                  <Package className="h-4 w-4" />
                  Products
                </TabsTrigger>
                <TabsTrigger value="raw" className="gap-2">
                  <Layers className="h-4 w-4" />
                  Raw Materials
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchTab === 'products' ? 'Search products...' : 'Search raw materials...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
          
          {/* Search Results */}
          {searchQuery && (
            <div className="max-h-48 overflow-y-auto border rounded-lg bg-background">
              {searchTab === 'products' ? (
                // Products list
                filteredProducts.length === 0 ? (
                  <div className="p-3 text-center text-sm text-muted-foreground">
                    No products found
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredProducts.slice(0, 10).map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleQuickAdd(product.id, false)}
                        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 text-left transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-10 h-10 rounded object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                              <Package className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-sm">{product.name}</p>
                            {product.sku && (
                              <p className="text-xs text-muted-foreground">{product.sku}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{formatCurrency(product.cost || product.price)}</p>
                          <p className="text-xs text-muted-foreground">Cost</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )
              ) : (
                // Raw items list
                <>
                  {filteredRawItems.length === 0 ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      No raw materials found
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredRawItems.slice(0, 10).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleQuickAdd(item.id, true)}
                          className="w-full flex items-center justify-between p-3 hover:bg-muted/50 text-left transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                              <Layers className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{item.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.sku ? `${item.sku} • ` : ''}{item.unit_of_measure}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">
                              {item.cost_per_unit ? formatCurrency(item.cost_per_unit) : '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">per {item.unit_of_measure}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {onCreateRawItem && searchQuery.trim() && (
                    <div className="border-t">
                      <button
                        type="button"
                        onClick={handleCreateAndAddRawItem}
                        disabled={creatingRawItem}
                        className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 text-left transition-colors text-sm"
                      >
                        {creatingRawItem ? (
                          <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                        ) : (
                          <Plus className="h-4 w-4 text-emerald-600" />
                        )}
                        <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                          Create &quot;{searchQuery.trim()}&quot; as new raw material
                        </span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Default quantity:</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={newQuantity}
                onChange={(e) => setNewQuantity(e.target.value)}
                className="w-28 h-8"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
              }}
            >
              Done
            </Button>
          </div>
        </div>
      )}

      {/* Components Table */}
      {components.length > 0 ? (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Component</th>
                <th className="px-3 py-2 text-right font-medium w-32">Quantity</th>
                <th className="px-3 py-2 text-right font-medium w-28">Cost</th>
                <th className="px-3 py-2 text-center font-medium w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {components.map((component) => {
                const isRawItem = !!component.rawItemId;
                const name = component.componentProduct?.name || component.rawItem?.name || 'Unknown';
                const sku = component.componentProduct?.sku || component.rawItem?.sku;
                const imageUrl = component.componentProduct?.image_url;
                const unit = component.rawItem?.unit_of_measure;
                
                let unitCost = 0;
                if (component.componentProduct) {
                  unitCost = component.componentProduct.cost || component.componentProduct.price;
                } else if (component.rawItem) {
                  unitCost = component.rawItem.cost_per_unit || 0;
                }
                const lineTotal = unitCost * component.quantity;

                return (
                  <tr key={component.id} className="hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        {isRawItem ? (
                          <div className="w-10 h-10 rounded bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <Layers className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                        ) : imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={name}
                            className="w-10 h-10 rounded object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{name}</p>
                            {isRawItem && (
                              <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-600">
                                Raw
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {sku || (unit ? `per ${unit}` : '')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <QuantityInput
                        value={component.quantity}
                        onChange={(val) => onUpdateQuantity(component.id, val)}
                        disabled={disabled}
                        unit={unit}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div>
                        <p className="font-medium">{formatCurrency(lineTotal)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(unitCost)} each
                        </p>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onRemoveComponent(component.id)}
                        disabled={disabled}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-muted/30">
              <tr className="font-medium">
                <td className="px-3 py-2" colSpan={2}>
                  <span className="text-muted-foreground">Total Cost</span>
                </td>
                <td className="px-3 py-2 text-right" colSpan={2}>
                  {formatCurrency(totals.cost)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 border rounded-lg border-dashed">
          <Package className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No components added yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Add Component" to search for products or raw materials
          </p>
        </div>
      )}

      {/* Info about quantity precision */}
      {components.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Tip: Use decimal quantities for ingredients (e.g., 0.33 lbs of rice per meal = 3 meals uses 1 lb)
        </p>
      )}

      {isLoading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}
    </div>
  );
}
